import type { TaxCalculationResult } from '#calculator/types'
import { TaxKey, type TaxMetadata } from '#dictionary'
import type { FilingStatus } from '#dictionary/filingStatuses'
import { TAX_BY_KEY } from '#dictionary/taxes'
import { getTaxThreshold, type TaxThreshold } from '#dictionary/taxThresholds'

import type {
  ITaxCalculator,
  TaxBracketBreakdown,
  TaxVarietyWithFilingStatus,
} from './types'

// TODO: #should predict further thresholds for each year
class AdditionalMedicareTax implements ITaxCalculator<TaxVarietyWithFilingStatus> {
  public readonly metadata: TaxMetadata = TAX_BY_KEY.get(
    TaxKey.TaxAdditionalMedicare
  )!

  private getThreshold(filingStatus: FilingStatus): TaxThreshold | undefined {
    return getTaxThreshold(TaxKey.TaxAdditionalMedicare, filingStatus)
  }

  public getTaxRate(
    _year: number,
    { filingStatus }: TaxVarietyWithFilingStatus
  ): number {
    return this.getThreshold(filingStatus)?.taxRate ?? 0
  }

  public getTaxableThreshold(
    _year: number,
    { filingStatus }: TaxVarietyWithFilingStatus
  ): number {
    return this.getThreshold(filingStatus)?.threshold ?? 0
  }

  public calculate(
    amount: number,
    year: number,
    { filingStatus }: TaxVarietyWithFilingStatus
  ): TaxCalculationResult {
    const threshold = this.getThreshold(filingStatus)
    if (!threshold || amount <= threshold.threshold) {
      return {
        taxMetadata: this.metadata,
        amount,
        taxableIncome: 0,
        standardDeduction: 0,
        effectiveRate: 0,
        totalTax: 0,
        bracketBreakdown: [],
      }
    }

    const taxableIncome = amount - threshold.threshold
    const totalTax = taxableIncome * threshold.taxRate
    const effectiveRate = totalTax / amount

    const bracket: TaxBracketBreakdown = {
      year: year,
      filingStatus: filingStatus,
      from: threshold.threshold,
      to: amount,
      taxRate: threshold.taxRate,
      description: `Additional Medicare tax on income above $${threshold.threshold.toLocaleString()}`,
      incomeInBracket: taxableIncome,
      taxInBracket: totalTax,
    }

    return {
      amount,
      taxMetadata: this.metadata,
      taxableIncome,
      standardDeduction: 0,
      effectiveRate,
      totalTax,
      bracketBreakdown: [bracket],
    }
  }
}

export default AdditionalMedicareTax
